import { ClientSecurityTransaction } from "@models/security-transactions/client-security-transaction";
import { SecurityTransaction } from "@models/security-transactions/security-transaction";
import {
  SecurityHoldingValidationError,
  SecurityHoldingValidationErrorMinusCurrentBalance,
  SecurityHoldingValidationErrorMinusHistoricalBalance
} from "@models/shares/security-holding-validation-error";
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from "@models/shares/security-registry-record";
import { ShareGroupView } from "@pages/shares/models/share-group-view";

export abstract class BaseSourcingResult {
  isValid: boolean;
  failedDocumentId: string | null;
  failedChangeId: string | null;

  constructor(sourcingResult?: Partial<BaseSourcingResult>) {
    this.isValid = sourcingResult?.isValid ?? true;
    this.failedDocumentId = sourcingResult?.failedDocumentId ?? null;
    this.failedChangeId = sourcingResult?.failedChangeId ?? null;
  }
}

export class AsicClassValidationError {
  class: string;
  totalNumber: number;
  totalPaid: number;
  totalUnpaid: number;
  asicTotalNumber: number;
  asicTotalPaid: number;
  asicTotalUnpaid: number;

  constructor(asicClassValidationError?: Partial<AsicClassValidationError>) {
    this.class = asicClassValidationError?.class ?? '';
    this.totalNumber = asicClassValidationError?.totalNumber ?? 0;
    this.totalPaid = asicClassValidationError?.totalPaid ?? 0;
    this.totalUnpaid = asicClassValidationError?.totalUnpaid ?? 0;
    this.asicTotalNumber = asicClassValidationError?.asicTotalNumber ?? 0;
    this.asicTotalPaid = asicClassValidationError?.asicTotalPaid ?? 0;
    this.asicTotalUnpaid = asicClassValidationError?.asicTotalUnpaid ?? 0;
  }
}

export class SecuritySourcingResult extends BaseSourcingResult {
  resultTransactions: SecurityTransaction[];
  resultSecurityRecords: SecurityRegistryRecord[];
  asicClassValidationErrors: AsicClassValidationError[];

  constructor(sourcingResult?: Partial<SecuritySourcingResult>) {
    super();

    this.resultTransactions = sourcingResult?.resultTransactions ? sourcingResult.resultTransactions.map((transaction) => new SecurityTransaction(transaction)) : [];
    this.resultSecurityRecords = sourcingResult?.resultSecurityRecords ? sourcingResult.resultSecurityRecords.map((record) => new SecurityRegistryRecord(record)) : [];
    this.asicClassValidationErrors = sourcingResult?.asicClassValidationErrors ? sourcingResult.asicClassValidationErrors.map((error) => new AsicClassValidationError(error)) : [];
  }

  toClientSecurityTransaction(showValidationError = false): ClientSecurityTransaction[] {
    const clientSecurityTransactions = this.resultTransactions.map(((transaction) => {
      const record = this.resultSecurityRecords.find((record) => record.holdings.find((holding) => holding.securityHoldingId == transaction.securityHoldingId));
      const relatedHolding = record?.holdings.find((holding) => holding.securityHoldingId == transaction.securityHoldingId);

      return new ClientSecurityTransaction({
        ...transaction,
        record,
        nonAsic: record?.nonAsic,
        holdingValidationErrors: relatedHolding && relatedHolding.securityHoldingId == transaction.securityHoldingId ? relatedHolding?.validationErrors : [],
        hash: relatedHolding?.hash ?? '',
        hashWithType: relatedHolding?.hashWithType ?? '',
        hashWithClass: relatedHolding?.hashWithClass ?? '',
        isBeneficialOwnerLabel: record?.isBeneficialOwner ? 'Yes' : 'No',
        validationError: record?.validationError,
        // validationError: showValidationError ? null : record?.validationError
      });
    }));

    const holdersByHash = new Map<string, (IndividualHolderModel | CorporateHolderModel)[]>();

    this.resultSecurityRecords.forEach((securityRegistryRecord) => {
      securityRegistryRecord.holdings.forEach((holding) => {
        holdersByHash.set(holding.hash, securityRegistryRecord.holders);

        const relatedTransactions = clientSecurityTransactions.find((transaction) => transaction.securityHoldingId == holding.securityHoldingId);
        if (relatedTransactions) {
          relatedTransactions.hash = holding.hash;
          relatedTransactions.hashWithType = holding.hashWithType;
          relatedTransactions.hashWithClass = holding.hashWithClass;
        }
      });
    });

    clientSecurityTransactions.forEach((transaction) => {
      const transactionHolders = holdersByHash.get(transaction.hash) ?? [];

      const beneficialOwnerNamesSet = transactionHolders
        .reduce((collection, holder) => {
          if (!holder.details.isBeneficialOwner) {
            collection.add(holder.details.beneficialOwner);
          }

          return collection;
        }, new Set<string>());
      const beneficialOwnerNames = [...beneficialOwnerNamesSet].join('; ');

      transaction.holders = transactionHolders;
      transaction.beneficialOwnerNames = beneficialOwnerNames;
    });

    return clientSecurityTransactions;
  }

  getHoldersWithNoHoldingsShareGroupViewRecord(): Record<string, ShareGroupView> {
    const holdersWithNoHoldingsShareGroupView: Record<string, ShareGroupView> = {};
    this.resultSecurityRecords
      .filter((record) => !record.holdings.length)
      .forEach((record) => {

        const recordHoldersRelationshipIdsString = record.holders.map((holder) => holder.relationshipId).join('');
        holdersWithNoHoldingsShareGroupView[recordHoldersRelationshipIdsString] = new ShareGroupView({
          shareholders: record.holders,
          nonAsic: record.nonAsic,
          shareholderName: '-',
          shareClass: record.validationError?.class,
          shareType: record.validationError?.class ?? '-',
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [],
          hash: '-',
          hashWithType: '-',
          hashWithClass: '-',

          hasIssue: !!record.validationError,
          numberOfSharesAsic: record.validationError?.asicTotalNumber ?? null,
        });
      });

    return holdersWithNoHoldingsShareGroupView;
  }

  toGroupByShareholders(clientSecurityTransaction: ClientSecurityTransaction[] = this.toClientSecurityTransaction()): ShareGroupView[] {
    const holdersWithNoHoldingsShareGroupView: Record<string, ShareGroupView> = this.getHoldersWithNoHoldingsShareGroupViewRecord();
    const groupedShareRows = clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareholders = share.holders;
      const existingGroupIndex = result.findIndex((group) => group.hash === share.hash);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareholders,
          shareholderName: share.name,
          nonAsic: share.nonAsic,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],
          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,

          hasIssue: false,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    Object.values(holdersWithNoHoldingsShareGroupView)
      .forEach((value) => groupedShareRows.push(value));

    return groupedShareRows;
  }

  toGroupByShareholderAndShareType(clientSecurityTransaction: ClientSecurityTransaction[] = this.toClientSecurityTransaction()): ShareGroupView[] {
    const holdersWithNoHoldingsShareGroupView: Record<string, ShareGroupView> = this.getHoldersWithNoHoldingsShareGroupViewRecord();

    const groupedShareRows = clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareholders = share.holders;
      const existingGroupIndex = result.findIndex((group) => group.hashWithType === share.hashWithType && group.hash === share.hash);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareholders,
          shareholderName: share.name,
          nonAsic: share.nonAsic,
          shareType: share.securityType.identifier,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],
          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    Object.values(holdersWithNoHoldingsShareGroupView)
      .forEach((value) => groupedShareRows.push(value));

    return groupedShareRows;
  }

  toGroupByShareholderAndShareClass(clientSecurityTransaction: ClientSecurityTransaction[] = this.toClientSecurityTransaction(), showUnlodgedChanges = false): ShareGroupView[] {
    const holdersWithNoHoldingsShareGroupView: Record<string, ShareGroupView> = this.getHoldersWithNoHoldingsShareGroupViewRecord();
    const groupedShareRows = clientSecurityTransaction.reduce((result: ShareGroupView[], share) => {
      const shareholders = share.holders;
      const existingGroupIndex = result.findIndex(group => group.hashWithClass === share.hashWithClass && group.hash === share.hash);

      if (existingGroupIndex !== -1) {
        result[existingGroupIndex].callRecords.push(share);
      } else {
        const groupView = new ShareGroupView({
          shareholders,
          shareholderName: share.name,
          nonAsic: share.nonAsic,
          shareClass: share.securityType.class ?? share.validationError?.class,
          numberOfShares: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          callRecords: [share],
          hash: share.hash,
          hashWithType: share.hashWithType,
          hashWithClass: share.hashWithClass,

          hasIssue: !!share.validationError,
          // hasIssue: !showUnlodgedChanges && !!share.validationError,
          issueMessage: 'The total aggregated figures of number of shares in the the registry does not match ASIC records. We recommend rectifying the register or using Form 492 to correct ASIC register if applicable ASAP',
          numberOfSharesAsic: share.validationError?.asicTotalNumber,
        });

        result.push(groupView);
      }
      return result;
    }, []);

    Object.values(holdersWithNoHoldingsShareGroupView)
      .forEach((value) => groupedShareRows.push(value));

    return groupedShareRows;
  }

  getSecurityHoldingValidationErrors(): SecurityHoldingValidationError[] {
    return this.resultSecurityRecords.flatMap((record) => record.holdings.flatMap((holding) => {
      const includeNegativeCurrentBalanceValidationError = holding.validationErrors.find((validationError) => validationError.$type == SecurityHoldingValidationErrorMinusCurrentBalance.$type);

      holding.validationErrors.forEach((validationError) => {
        validationError.holdersRelationshipIds = record.holders.map((holder) => holder.relationshipId).filter((Boolean));
        validationError.message = (validationError.constructor as typeof SecurityHoldingValidationError).getMessage(record);
      });

      if (includeNegativeCurrentBalanceValidationError) {
        return holding.validationErrors.filter((validationError) => validationError.$type !== SecurityHoldingValidationErrorMinusHistoricalBalance.$type);
      }

      return holding.validationErrors;
    }));
  }
}

